/**
 * Dealer Branding Tab
 * V3: SVG-based CTA Templates with outlined text paths
 * Templates: script_yellow_arrow, start_here_swoosh, start_here_box
 * 
 * BUILD: 1fd008d067fde675dfb82395d161a263315594a0 @ 2026-01-23T20:02:08Z
 */
import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { RefreshCw, Pipette } from "lucide-react";
import { AnimatedCtaPreview } from "./AnimatedCtaPreview";
import { DomDiagnosticCard } from "./DomDiagnosticCard";

// Build info for debugging
const COMPONENT_BUILD = "DealerBrandingTab-v1.3.0-P0-PROD-FIX-20260126";

// CTA Template definitions - V3 SVG templates with outlined text
const CTA_TEMPLATES = [
  {
    id: "script_yellow_arrow",
    name: "Script + Yellow Arrow",
    description: '"Get Our Best Price!" with yellow curved arrow',
    defaultButton: "Unlock Best Price",
    defaultColors: {
      btn_bg: "#16a34a",
      btn_text: "#ffffff",
      headline: "#2563eb",
      arrow: "#facc15"
    }
  },
  {
    id: "start_here_swoosh",
    name: "START HERE + Swoosh",
    description: '"Want a Lower Price? START HERE!" with red swoosh',
    defaultButton: "Unlock Instant Price",
    defaultColors: {
      btn_bg: "#dc2626",
      btn_text: "#ffffff",
      headline: "#e11d48",
      subhead: "#111827",
      arrow: "#e11d48"
    }
  },
  {
    id: "start_here_box",
    name: "START HERE + Box",
    description: '"Want a Lower Price? START HERE!" with boxed button',
    defaultButton: "Unlock Instant Price",
    defaultColors: {
      btn_bg: "#16a34a",
      btn_text: "#ffffff",
      headline: "#16a34a",
      subhead: "#111827",
      arrow: "#16a34a"
    }
  },
  {
    id: "wow_price_arrow",
    name: "GET WOW! PRICE",
    description: '"Want a Lower Price? GET WOW! PRICE" with curved arrow',
    defaultButton: "Get Today's Best Price",
    defaultColors: {
      btn_bg: "#dc2626",
      btn_text: "#ffffff",
      headline: "#374151",
      subhead: "#dc2626",
      arrow: "#111827"
    }
  },
  {
    id: "best_price_arrow",
    name: "GET OUR BEST PRICE!",
    description: '"GET OUR BEST PRICE!" with down arrow - bold red',
    defaultButton: "Get Today's Best Price",
    defaultColors: {
      btn_bg: "#dc2626",
      btn_text: "#ffffff",
      headline: "#dc2626",
      subhead: "#dc2626",
      arrow: "#dc2626"
    }
  },
  {
    id: "simple_box",
    name: "Simple Box (Customizable)",
    description: 'Clean button with lock icon - fully customizable text & colors',
    defaultButton: "🔒 Request More Information",
    defaultColors: {
      btn_bg: "#1f2937",
      btn_text: "#ffffff"
    }
  },
  {
    id: "button_rounded",
    name: "Button Only (Rounded)",
    description: "Clean rounded button with lock badge - no callout",
    defaultButton: "Unlock Best Price",
    defaultColors: {
      btn_bg: "#16a34a",
      btn_text: "#ffffff"
    }
  },
  {
    id: "button_squared",
    name: "Button Only (Squared)",
    description: "Clean squared button with lock badge - no callout",
    defaultButton: "Unlock Best Price",
    defaultColors: {
      btn_bg: "#16a34a",
      btn_text: "#ffffff"
    }
  },
  // ============== Animated Premium Templates (v2) ==============
  {
    id: "frosted_glass",
    name: "Frosted Glass Card",
    description: "Translucent frosted overlay with shimmer-sweep button",
    defaultButton: "Unlock Best Price",
    defaultColors: {
      btn_bg: "#0F172A",
      btn_text: "#FFFFFF",
      headline: "#0F172A",
      overlay_bg: "rgba(255,255,255,0.65)",
      frame_bg: "#4338CA"
    }
  },
  {
    id: "urgency_strip",
    name: "Urgency Strip",
    description: "Full-width dark banner with pulsing red dot, slides in from below",
    defaultButton: "Unlock Best Price",
    defaultColors: {
      btn_bg: "#DC2626",
      btn_text: "#FFFFFF",
      headline: "#FFFFFF",
      banner_bg: "#0F172A",
      dot: "#EF4444"
    }
  },
  {
    id: "split_badge",
    name: "Split Badge",
    description: "Two-tone badge — animated lock icon on the left, button on the right",
    defaultButton: "See Today's Price",
    defaultColors: {
      btn_bg: "#16A34A",
      btn_text: "#FFFFFF",
      badge_bg: "#0F172A",
      badge_text: "#FACC15"
    }
  },
  {
    id: "neon_glow",
    name: "Neon Glow",
    description: "Dark overlay with pulsing neon-colored button border (heartbeat)",
    defaultButton: "Unlock Best Price",
    defaultColors: {
      btn_bg: "#0B1120",
      btn_text: "#A5F3FC",
      glow: "#22D3EE",
    }
  },
  {
    id: "minimal_float",
    name: "Minimal Float",
    description: "Premium pill button with bouncing lock icon — no headline",
    defaultButton: "Unlock Best Price",
    defaultColors: {
      btn_bg: "#111827",
      btn_text: "#FFFFFF",
    }
  },
  // iter123 — additive only, don't modify existing entries above.
  {
    id: "blurred_price_reveal",
    name: "Blurred Price Reveal",
    description: "Shows MSRP plus a blurred 'Your Price' — clicking unblurs to a teaser and opens the form. Strongest pull when shoppers see SOME number to be curious about.",
    defaultButton: "Reveal Your Price",
    defaultColors: {
      btn_bg: "#166CA2",
      btn_text: "#FFFFFF",
      headline: "#0F172A",
      muted: "#6B7280",
      card_bg: "#FFFFFF",
      accent: "#166CA2",
    }
  },
  {
    id: "live_activity",
    name: "Live Activity",
    description: "Pulsing 'X shoppers viewing, Y prices unlocked' line above the button. Numbers update every 60 seconds from your real traffic + unlock data, with a smart floor so the line never reads '0' on quiet days.",
    defaultButton: "Unlock Your Price",
    defaultColors: {
      btn_bg: "#0F172A",
      btn_text: "#FFFFFF",
      muted: "#475569",
      dot: "#DC2626",
    }
  },
  {
    id: "savings_first",
    name: "Savings-First",
    description: "Flips the framing to the gain instead of the lock: 'You could save $X below MSRP' → Unlock Your Savings.",
    defaultButton: "Unlock Your Savings",
    defaultColors: {
      btn_bg: "#10B981",
      btn_text: "#FFFFFF",
      headline: "#064E3B",
      card_bg: "#ECFDF5",
      accent: "#10B981",
    }
  },
];

// Lock icon SVG
const LockIcon = ({ className = "w-4 h-4", color = "currentColor" }) => (
  <svg className={className} viewBox="0 0 24 24" fill={color}>
    <path d="M12 1C8.676 1 6 3.676 6 7v2H4v14h16V9h-2V7c0-3.324-2.676-6-6-6zm-4 6c0-2.206 1.794-4 4-4s4 1.794 4 4v2H8V7zm6 10h-4v-5h4v5z"/>
  </svg>
);

// SVG Template Thumbnails - rendered as actual SVGs
const SvgThumbnailScriptYellowArrow = ({ colors }) => (
  <svg viewBox="0 0 100 50" className="w-full h-full">
    {/* Yellow arrow */}
    <path 
      d="M15 8 C10 10, 8 18, 6 28 L10 24 M6 28 L3 23" 
      fill="none" 
      stroke={colors.arrow || "#facc15"} 
      strokeWidth="3" 
      strokeLinecap="round"
    />
    {/* Script text "Get Our Best..." */}
    <text 
      x="55" 
      y="25" 
      textAnchor="middle" 
      fontSize="11" 
      fontStyle="italic" 
      fill={colors.headline || "#2563eb"}
      fontFamily="cursive"
    >
      Get Our Best...
    </text>
    {/* Button */}
    <rect x="20" y="32" width="60" height="14" rx="7" fill={colors.btn_bg || "#16a34a"}/>
  </svg>
);

const SvgThumbnailStartHereSwoosh = ({ colors }) => (
  <svg viewBox="0 0 100 55" className="w-full h-full">
    {/* Script headline */}
    <text 
      x="50" 
      y="14" 
      textAnchor="middle" 
      fontSize="9" 
      fontStyle="italic" 
      fill={colors.headline || "#e11d48"}
      fontFamily="cursive"
    >
      Want a Lower...
    </text>
    {/* START HERE text */}
    <text 
      x="42" 
      y="30" 
      textAnchor="middle" 
      fontSize="11" 
      fontWeight="900" 
      fill={colors.subhead || "#111827"}
    >
      START HERE!
    </text>
    {/* Swoosh arrow */}
    <path 
      d="M75 12 Q88 15, 92 28 Q94 35, 90 42 M85 35 L90 42 L96 37" 
      fill="none" 
      stroke={colors.arrow || "#e11d48"} 
      strokeWidth="2.5" 
      strokeLinecap="round"
    />
    {/* Button */}
    <rect x="15" y="38" width="70" height="13" rx="6" fill={colors.btn_bg || "#dc2626"}/>
  </svg>
);

const SvgThumbnailStartHereBox = ({ colors }) => (
  <svg viewBox="0 0 100 55" className="w-full h-full">
    {/* Script headline */}
    <text 
      x="50" 
      y="13" 
      textAnchor="middle" 
      fontSize="9" 
      fontStyle="italic" 
      fill={colors.headline || "#16a34a"}
      fontFamily="cursive"
    >
      Want a Lower...
    </text>
    {/* START HERE text + arrow */}
    <text 
      x="45" 
      y="28" 
      textAnchor="middle" 
      fontSize="10" 
      fontWeight="900" 
      fill={colors.subhead || "#111827"}
    >
      START HERE!
    </text>
    {/* Down arrow */}
    <path 
      d="M77 22 L77 33 M73 29 L77 33 L81 29" 
      fill="none" 
      stroke={colors.arrow || "#16a34a"} 
      strokeWidth="2" 
      strokeLinecap="round"
    />
    {/* Boxed button */}
    <rect x="15" y="36" width="70" height="14" rx="3" fill={colors.btn_bg || "#16a34a"} stroke="#111827" strokeWidth="1.5"/>
  </svg>
);

const SvgThumbnailWowPriceArrow = ({ colors }) => (
  <svg viewBox="0 0 100 70" className="w-full h-full">
    {/* Define proportional arrowhead marker */}
    <defs>
      <marker 
        id="vdArrowHead-thumb" 
        markerUnits="strokeWidth"
        markerWidth="2.2" 
        markerHeight="2.2" 
        refX="2.0" 
        refY="1.1" 
        orient="auto"
      >
        <path d="M0,0 L2.2,1.1 L0,2.2 Z" fill={colors.arrow || "#111827"} />
      </marker>
    </defs>
    {/* Top question */}
    <text 
      x="50" 
      y="12" 
      textAnchor="middle" 
      fontSize="8" 
      fontStyle="italic" 
      fill={colors.headline || "#374151"}
    >
      Want a Lower...
    </text>
    {/* GET text */}
    <text 
      x="25" 
      y="28" 
      textAnchor="middle" 
      fontSize="10" 
      fontWeight="900" 
      fill={colors.subhead || "#dc2626"}
    >
      GET
    </text>
    {/* WOW! text - largest */}
    <text 
      x="35" 
      y="45" 
      textAnchor="middle" 
      fontSize="16" 
      fontWeight="900" 
      fill={colors.subhead || "#dc2626"}
    >
      WOW!
    </text>
    {/* PRICE text */}
    <text 
      x="50" 
      y="55" 
      textAnchor="middle" 
      fontSize="9" 
      fontWeight="900" 
      fill={colors.subhead || "#dc2626"}
    >
      PRICE
    </text>
    {/* Curved arrow with proportional arrowhead */}
    <path 
      d="M68 18 C88 20, 92 38, 86 52" 
      fill="none" 
      stroke={colors.arrow || "#111827"} 
      strokeWidth="2" 
      strokeLinecap="round"
      strokeLinejoin="round"
      markerEnd="url(#vdArrowHead-thumb)"
    />
    {/* Button */}
    <rect x="15" y="58" width="70" height="10" rx="3" fill={colors.btn_bg || "#dc2626"}/>
  </svg>
);

// Button-only templates (no callout)
const SvgThumbnailButtonRounded = ({ colors }) => (
  <svg viewBox="0 0 100 40" className="w-full h-full">
    {/* Rounded button with lock badge */}
    <rect x="5" y="10" width="90" height="24" rx="12" fill={colors.btn_bg || "#16a34a"}/>
    {/* Lock badge circle */}
    <circle cx="22" cy="22" r="8" fill="rgba(255,255,255,0.95)"/>
    {/* Lock icon in badge */}
    <path 
      d="M22 18 C20.5 18 19.5 19 19.5 20.5 L19.5 21 L19 21 L19 25 L25 25 L25 21 L24.5 21 L24.5 20.5 C24.5 19 23.5 18 22 18 Z M21 20.5 C21 19.7 21.5 19.2 22 19.2 C22.5 19.2 23 19.7 23 20.5 L23 21 L21 21 Z"
      fill={colors.btn_bg || "#16a34a"}
    />
    {/* Text placeholder */}
    <text x="58" y="26" textAnchor="middle" fontSize="7" fontWeight="700" fill={colors.btn_text || "#ffffff"}>
      Unlock Best...
    </text>
  </svg>
);

const SvgThumbnailButtonSquared = ({ colors }) => (
  <svg viewBox="0 0 100 40" className="w-full h-full">
    {/* Squared button with lock badge */}
    <rect x="5" y="10" width="90" height="24" rx="4" fill={colors.btn_bg || "#16a34a"}/>
    {/* Lock badge circle */}
    <circle cx="22" cy="22" r="8" fill="rgba(255,255,255,0.95)"/>
    {/* Lock icon in badge */}
    <path 
      d="M22 18 C20.5 18 19.5 19 19.5 20.5 L19.5 21 L19 21 L19 25 L25 25 L25 21 L24.5 21 L24.5 20.5 C24.5 19 23.5 18 22 18 Z M21 20.5 C21 19.7 21.5 19.2 22 19.2 C22.5 19.2 23 19.7 23 20.5 L23 21 L21 21 Z"
      fill={colors.btn_bg || "#16a34a"}
    />
    {/* Text placeholder */}
    <text x="58" y="26" textAnchor="middle" fontSize="7" fontWeight="700" fill={colors.btn_text || "#ffffff"}>
      Unlock Best...
    </text>
  </svg>
);

// ============== Thumbnails: Animated Premium Templates (v2) ==============
// IMPORTANT: viewBox is 100x50 (or 100x40 for split_badge). Font sizes 9-11
// match the other thumbnails for consistent legibility at the actual rendered
// thumbnail size (~140x70). Production rendering on dealer sites is unaffected
// — these are JUST the picker previews.
const SvgThumbnailFrostedGlass = ({ colors }) => (
  <svg viewBox="0 0 100 50" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
    <defs>
      <linearGradient id="vd-thumb-bg-fg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#93C5FD" />
        <stop offset="100%" stopColor="#4338CA" />
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="100" height="50" fill="url(#vd-thumb-bg-fg)" />
    <rect x="6" y="6" width="88" height="38" rx="5" fill="rgba(255,255,255,0.78)" />
    <text x="50" y="18" textAnchor="middle" fontSize="9" fontWeight="800"
          letterSpacing="0.4" fill={colors.headline || "#0F172A"}>
      EXCLUSIVE PRICE
    </text>
    <rect x="12" y="24" width="76" height="16" rx="8" fill={colors.btn_bg || "#0F172A"} />
    <text x="50" y="35" textAnchor="middle" fontSize="9" fontWeight="700"
          fill={colors.btn_text || "#FFFFFF"}>
      Unlock Price
    </text>
  </svg>
);

const SvgThumbnailUrgencyStrip = ({ colors }) => (
  <svg viewBox="0 0 100 50" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
    <rect x="0" y="0" width="100" height="50" fill="#E5E7EB" />
    {/* Banner spans the bottom half */}
    <rect x="0" y="14" width="100" height="22" fill={colors.banner_bg || "#0F172A"} />
    {/* Pulsing dot */}
    <circle cx="9" cy="25" r="3" fill={colors.dot || "#EF4444"} />
    <circle cx="9" cy="25" r="5" fill={colors.dot || "#EF4444"} fillOpacity="0.25" />
    {/* Banner text */}
    <text x="16" y="29" fontSize="8" fontWeight="800"
          fill={colors.headline || "#FFFFFF"}>
      LIMITED
    </text>
    {/* Button */}
    <rect x="56" y="18" width="40" height="14" rx="3" fill={colors.btn_bg || "#DC2626"} />
    <text x="76" y="28" textAnchor="middle" fontSize="8" fontWeight="800"
          fill={colors.btn_text || "#FFFFFF"}>
      UNLOCK
    </text>
  </svg>
);

const SvgThumbnailSplitBadge = ({ colors }) => (
  <svg viewBox="0 0 100 40" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
    {/* Left badge half */}
    <rect x="2" y="6" width="44" height="28" rx="4" fill={colors.badge_bg || "#0F172A"} />
    {/* Lock icon (sized properly) */}
    <g transform="translate(7, 12)" stroke={colors.badge_text || "#FACC15"} strokeWidth="1.6" fill="none" strokeLinejoin="round" strokeLinecap="round">
      <rect x="0" y="6" width="9" height="9" rx="1.5" />
      <path d="M2 6 V3.5 a2.5 2.5 0 0 1 5 0 V6" />
    </g>
    <text x="32" y="24" textAnchor="middle" fontSize="8" fontWeight="800"
          fill={colors.badge_text || "#FACC15"}>
      LOCK
    </text>
    {/* Right button half */}
    <rect x="48" y="6" width="50" height="28" rx="4" fill={colors.btn_bg || "#16A34A"} />
    <text x="73" y="24" textAnchor="middle" fontSize="9" fontWeight="700"
          fill={colors.btn_text || "#FFFFFF"}>
      See Price
    </text>
  </svg>
);

const SvgThumbnailNeonGlow = ({ colors }) => (
  <svg viewBox="0 0 100 50" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
    <defs>
      <filter id="vd-neon-thumb" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="1.2" />
      </filter>
    </defs>
    <rect x="0" y="0" width="100" height="50" fill="#0B1120" />
    {/* Soft glow */}
    <rect x="10" y="12" width="80" height="26" rx="13"
          fill="none" stroke={colors.glow || "#22D3EE"} strokeWidth="3"
          opacity="0.45" filter="url(#vd-neon-thumb)" />
    {/* Crisp border */}
    <rect x="10" y="12" width="80" height="26" rx="13"
          fill={colors.btn_bg || "#0B1120"}
          stroke={colors.glow || "#22D3EE"} strokeWidth="1.8" />
    <text x="50" y="29" textAnchor="middle" fontSize="9" fontWeight="800"
          letterSpacing="0.5" fill={colors.btn_text || "#A5F3FC"}>
      UNLOCK PRICE
    </text>
  </svg>
);

const SvgThumbnailMinimalFloat = ({ colors }) => (
  <svg viewBox="0 0 100 50" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
    <rect x="0" y="0" width="100" height="50" fill="#F8FAFC" />
    {/* Soft shadow under the floating pill */}
    <ellipse cx="50" cy="42" rx="30" ry="2.5" fill="rgba(15,23,42,0.18)" />
    <rect x="10" y="14" width="80" height="22" rx="11" fill={colors.btn_bg || "#111827"} />
    {/* Lock icon */}
    <g transform="translate(18, 18)" stroke={colors.btn_text || "#FFFFFF"} strokeWidth="1.6" fill="none" strokeLinejoin="round" strokeLinecap="round">
      <rect x="0" y="6" width="10" height="9" rx="1.5" />
      <path d="M2.5 6 V3.5 a2.5 2.5 0 0 1 5 0 V6" />
    </g>
    <text x="58" y="29" textAnchor="middle" fontSize="9" fontWeight="700"
          fill={colors.btn_text || "#FFFFFF"}>
      Unlock Price
    </text>
  </svg>
);

// iter123 — Blurred Price Reveal thumbnail
const SvgThumbnailBlurredPriceReveal = ({ colors }) => (
  <svg viewBox="0 0 100 50" className="w-full h-full">
    <rect x="2" y="2" width="96" height="46" rx="4" fill={colors.card_bg || "#FFFFFF"} stroke="rgba(15,23,42,0.08)" />
    <text x="6" y="12" fontSize="6" fill={colors.muted || "#6B7280"}>MSRP $48,990</text>
    <text x="6" y="22" fontSize="7" fontWeight="700" fill={colors.headline || "#0F172A"}>Your Price:</text>
    <g filter="url(#vd-blur)">
      <text x="32" y="22" fontSize="7" fontWeight="700" fill={colors.accent || "#166CA2"}>$XX,XXX</text>
    </g>
    <defs>
      <filter id="vd-blur"><feGaussianBlur stdDeviation="1" /></filter>
    </defs>
    <rect x="6" y="28" width="88" height="14" rx="7" fill={colors.btn_bg || "#166CA2"} />
    <text x="50" y="38" textAnchor="middle" fontSize="7" fontWeight="700" fill={colors.btn_text || "#FFFFFF"}>Reveal Your Price</text>
  </svg>
);

// iter123 — Live Activity thumbnail
const SvgThumbnailLiveActivity = ({ colors }) => (
  <svg viewBox="0 0 100 50" className="w-full h-full">
    <rect x="0" y="0" width="100" height="50" fill="#F8FAFC" />
    <circle cx="14" cy="12" r="2" fill={colors.dot || "#DC2626"}>
      <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
    </circle>
    <text x="20" y="14" fontSize="5.5" fill={colors.muted || "#475569"}>7 viewing · 3 unlocked today</text>
    <rect x="6" y="22" width="88" height="20" rx="3" fill={colors.btn_bg || "#0F172A"} />
    <text x="50" y="35" textAnchor="middle" fontSize="8" fontWeight="700" fill={colors.btn_text || "#FFFFFF"}>Unlock Your Price</text>
  </svg>
);

// iter123 — Savings First thumbnail
const SvgThumbnailSavingsFirst = ({ colors }) => (
  <svg viewBox="0 0 100 50" className="w-full h-full">
    <rect x="2" y="2" width="96" height="46" rx="4" fill={colors.card_bg || "#ECFDF5"} stroke={colors.accent || "#10B981"} />
    <text x="6" y="14" fontSize="6" fill={colors.headline || "#064E3B"}>You could save</text>
    <text x="38" y="14" fontSize="8" fontWeight="800" fill={colors.accent || "#10B981"}>$3,200</text>
    <text x="6" y="22" fontSize="6" fill={colors.headline || "#064E3B"}>below MSRP</text>
    <rect x="6" y="28" width="88" height="14" rx="3" fill={colors.btn_bg || "#10B981"} />
    <text x="50" y="38" textAnchor="middle" fontSize="7" fontWeight="700" fill={colors.btn_text || "#FFFFFF"}>Unlock Your Savings</text>
  </svg>
);

// Thumbnail for best_price_arrow template
const SvgThumbnailBestPriceArrow = ({ colors }) => (
  <svg viewBox="0 0 100 50" className="w-full h-full">
    {/* GET OUR text */}
    <text x="50" y="14" textAnchor="middle" fontSize="10" fontWeight="900" fill={colors.subhead || "#dc2626"} fontFamily="Arial Black, sans-serif">
      GET OUR
    </text>
    {/* BEST PRICE! text */}
    <text x="50" y="26" textAnchor="middle" fontSize="10" fontWeight="900" fill={colors.subhead || "#dc2626"} fontFamily="Arial Black, sans-serif">
      BEST PRICE!
    </text>
    {/* Down arrow */}
    <path 
      d="M50 30 L50 42 M44 38 L50 42 L56 38" 
      fill="none" 
      stroke={colors.arrow || "#dc2626"} 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Thumbnail for simple_box template
const SvgThumbnailSimpleBox = ({ colors }) => (
  <svg viewBox="0 0 100 40" className="w-full h-full">
    {/* Simple rounded box button */}
    <rect x="5" y="10" width="90" height="24" rx="6" fill={colors.btn_bg || "#1f2937"}/>
    {/* Lock emoji + text */}
    <text x="50" y="26" textAnchor="middle" fontSize="7" fontWeight="600" fill={colors.btn_text || "#ffffff"}>
      🔒 Request Info
    </text>
  </svg>
);

// iter120 — Eyedropper helper.
// Delegates dominant-color extraction to the backend (`/api/cta/dominant-color`)
// because dealer logos are typically served from CDNs without permissive CORS
// headers, which taints a client-side canvas. The backend uses Pillow on the
// downloaded image and returns `{ hex: "#RRGGBB" }`.

const DealerBrandingTab = ({ dealer, unlockSettings, onRefresh, disabled, endpointMode = "admin" }) => {
  const { apiFetch } = useAuth();
  const { success: showToast, error: showError } = useToast();
  const [saving, setSaving] = useState(false);
  // iter120 — Eyedropper state: which color field is currently auto-picking
  // from the logo, so we can disable the button + show a spinner.
  const [pickingField, setPickingField] = useState(null);

  // iter99 — When mounted on the dealer's own UnlockSettingsPage we use the
  // dealer-self endpoints (/unlock/settings) instead of admin endpoints.
  // The two surfaces share the SAME payload shape.
  const isDealerSelf = endpointMode === "dealer-self";
  const unlockSettingsApiPath = isDealerSelf
    ? "/unlock/settings"
    : `/admin/dealers/${dealer?.id}/unlock-settings`;
  const verifyDealerApiPath = isDealerSelf
    ? "/unlock/settings"
    : `/admin/dealers/${dealer?.id}`;
  
  // Parse cta_colors from JSON or use defaults
  const parseCtaColors = (ctaColorsJson) => {
    try {
      if (ctaColorsJson) {
        return typeof ctaColorsJson === 'string' ? JSON.parse(ctaColorsJson) : ctaColorsJson;
      }
    } catch (e) { /* fall through to defaults */ }
    return {
      btn_bg: "#16a34a",
      btn_text: "#ffffff",
      btn_icon: "#ffffff",
      headline: "#2563eb",
      subhead: "#111827",
      arrow: "#facc15"
    };
  };
  
  const [settings, setSettings] = useState({
    enabled: false,
    injection_mode: "auto",
    site_adapter: null,
    cta_template_id: "script_yellow_arrow",
    cta_script_text_key: null,  // Additive: top headline text dropdown
    cta_custom_headline_text: "",  // iter125: free-text headline when key === "__CUSTOM__"
    cta_headline_scale: 1.0,    // Additive: headline size slider (0.8-1.3)
    cta_colors: {
      btn_bg: "#16a34a",
      btn_text: "#ffffff",
      btn_icon: "#ffffff",
      headline: "#2563eb",
      subhead: "#111827",
      arrow: "#facc15"
    },
    button_text: "Unlock Best Price",
    subtext: "Enter your details to reveal your personalized price",
    reveal_content: "Your exclusive price is ready!",
    unlock_price: "",  // The actual price to reveal (e.g., "$24,995")
    require_sms_verification: true,
    require_email: true,
    require_first_name: true,
    require_last_name: false,
    // iter69 — unlock persistence (24h=1, 72h=3, 7d=7, 30d=30)
    unlock_persist_days: 30,
    // Dealer Permission
    dealer_unlock_control_enabled: false,
  });
  
  // Top Headline Text options (exact 6 presets + Custom).
  // iter125 — the "__CUSTOM__" sentinel unlocks a free-text input so the
  // dealer can type whatever they want (still constrained to templates
  // that actually render this headline — see HEADLINE_TEMPLATES below).
  const SCRIPT_TEXT_OPTIONS = [
    { key: "", label: "(Use template default)" },
    { key: "WANT_A_LOWER_PRICE", label: "Want a Lower Price?" },
    { key: "GET_OUR_BEST_PRICE", label: "Get Our Best Price!" },
    { key: "UNLOCK_YOUR_BEST_PRICE", label: "Unlock Your Best Price" },
    { key: "SEE_TODAYS_PRICE", label: "See Today's Price" },
    { key: "CLICK_TO_REVEAL_PRICE", label: "Click to Reveal Price" },
    { key: "START_HERE", label: "Start Here" },
    { key: "__CUSTOM__", label: "✏️ Custom (type your own)…" },
  ];
  
  // Brand color scraper (optional helper) — URL is persisted per-dealer in
  // localStorage so the master admin can come back and refresh the palette
  // without retyping it.
  const websiteStorageKey = dealer?.id ? `vd_branding_url_${dealer.id}` : null;
  const colorsStorageKey = dealer?.id ? `vd_branding_colors_${dealer.id}` : null;
  const [dealerWebsite, setDealerWebsite] = useState(() => {
    if (typeof window === "undefined" || !websiteStorageKey) return "";
    return window.localStorage.getItem(websiteStorageKey) || "";
  });
  const [scrapedColors, setScrapedColors] = useState(() => {
    if (typeof window === "undefined" || !colorsStorageKey) return [];
    try {
      const raw = window.localStorage.getItem(colorsStorageKey);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [scraping, setScraping] = useState(false);
  const [scrapeMessage, setScrapeMessage] = useState("");
  
  // Site adapter editing state
  const [showSiteAdapter, setShowSiteAdapter] = useState(false);
  const [siteAdapterJson, setSiteAdapterJson] = useState("");

  // iter86 — "Copy branding from another dealer" modal state.
  // Master admin opens this from the top of the Branding tab, picks a
  // source dealer, confirms, and we POST to the copy endpoint. After
  // success we call onRefresh() so the form rehydrates with the new
  // values. Modifies ONLY the dealer currently being viewed.
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyDealers, setCopyDealers] = useState([]);
  const [copyDealersLoading, setCopyDealersLoading] = useState(false);
  const [copySelectedSourceId, setCopySelectedSourceId] = useState("");
  const [copyApplying, setCopyApplying] = useState(false);

  const openCopyModal = async () => {
    if (disabled) {
      showError("Cannot edit settings for a disabled dealer");
      return;
    }
    setCopyModalOpen(true);
    setCopySelectedSourceId("");
    if (copyDealers.length > 0) return;
    setCopyDealersLoading(true);
    try {
      // Pull a generous page — most accounts have <100 dealers. If a
      // reseller exceeds this we can add server-side search later.
      const res = await apiFetch("/admin/dealers?limit=200", { method: "GET" });
      const list = res.data?.dealers || res.data?.items || [];
      // Exclude the current dealer and any archived rooftops from the
      // dropdown so the master admin can't pick an invalid source.
      const filtered = list.filter(d => d.id !== dealer.id && !d.archived);
      filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setCopyDealers(filtered);
    } catch (err) {
      showError(err?.response?.data?.detail || "Failed to load dealer list");
    } finally {
      setCopyDealersLoading(false);
    }
  };

  const applyCopyBranding = async () => {
    if (!copySelectedSourceId) {
      showError("Pick a source dealer first");
      return;
    }
    const source = copyDealers.find(d => d.id === copySelectedSourceId);
    if (!window.confirm(
      `Copy CTA branding from "${source?.name || "selected dealer"}" onto "${dealer.name}"? ` +
      `This will overwrite the template, colors, button text, headline text, ` +
      `reveal content and logo. Pricing rules and dealer permissions are NOT affected.`
    )) return;
    setCopyApplying(true);
    try {
      const res = await apiFetch(`/admin/dealers/${dealer.id}/copy-branding-from`, {
        method: "POST",
        data: { source_dealer_id: copySelectedSourceId },
      });
      const copied = res.data?.copied_fields?.length || 0;
      showToast(`Copied ${copied} branding fields from ${source?.name}. Click "Save Settings" if you tweak anything further.`);
      setCopyModalOpen(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      showError(err?.response?.data?.detail || "Failed to copy branding");
    } finally {
      setCopyApplying(false);
    }
  };

  useEffect(() => {
    if (unlockSettings) {
      const colors = parseCtaColors(unlockSettings.cta_colors);
      
      // Apply legacy color mapping if no cta_colors
      if (!unlockSettings.cta_colors) {
        colors.btn_bg = unlockSettings.button_color || unlockSettings.primary_color || colors.btn_bg;
        colors.btn_text = unlockSettings.text_color || colors.btn_text;
        colors.headline = unlockSettings.accent_color || colors.headline;
        colors.arrow = unlockSettings.accent_color || colors.arrow;
      }
      
      // Parse site adapter
      let siteAdapter = null;
      try {
        siteAdapter = unlockSettings.site_adapter ? JSON.parse(unlockSettings.site_adapter) : null;
      } catch (e) { /* keep site_adapter null on parse failure */ }
      setSiteAdapterJson(siteAdapter ? JSON.stringify(siteAdapter, null, 2) : "");
      
      const newUnlockPrice = unlockSettings.unlock_price || "";
      
      setSettings(prev => ({
        ...prev,
        enabled: unlockSettings.enabled,
        injection_mode: unlockSettings.injection_mode || "auto",
        site_adapter: siteAdapter,
        cta_template_id: unlockSettings.cta_template_id || mapLegacyTemplate(unlockSettings.cta_template) || "script_yellow_arrow",
        cta_script_text_key: unlockSettings.cta_script_text_key || "",
        cta_custom_headline_text: unlockSettings.cta_custom_headline_text || "",  // iter125
        cta_headline_scale: unlockSettings.cta_headline_scale || 1.0,
        cta_colors: colors,
        button_text: unlockSettings.button_text || "Unlock Instant Price",
        subtext: unlockSettings.subtext,
        reveal_content: unlockSettings.reveal_content,
        unlock_price: newUnlockPrice,
        require_sms_verification: unlockSettings.require_sms_verification,
        require_email: unlockSettings.require_email,
        require_first_name: unlockSettings.require_first_name ?? true,
        require_last_name: unlockSettings.require_last_name ?? false,
        // iter69 — unlock persistence dropdown (24h/72h/7d/30d)
        unlock_persist_days: unlockSettings.unlock_persist_days ?? 30,
        // Unlock notification recipients (list of emails)
        unlock_notify_emails: Array.isArray(unlockSettings.unlock_notify_emails)
          ? unlockSettings.unlock_notify_emails
          : [],
        // Dealer Permission
        dealer_unlock_control_enabled: unlockSettings.dealer_unlock_control_enabled || false,
      }));
    }
  }, [unlockSettings, dealer]);

  // Fetch pricing rules removed — Flexible Pricing now lives in dedicated
  // Pricing (New) and Pricing (Used) tabs on the dealer detail page.

  // Map legacy template names to new IDs
  const mapLegacyTemplate = (legacy) => {
    const mapping = {
      'best_price_script': 'script_yellow_arrow',
      'start_here_swoosh': 'start_here_swoosh',
      'start_here_boxed': 'start_here_box',
      'start_here_bold_arrow': 'start_here_swoosh',
      'minimal_directional': 'script_yellow_arrow'
    };
    return mapping[legacy] || null;
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleColorChange = (colorKey, value) => {
    setSettings(prev => ({
      ...prev,
      cta_colors: { ...prev.cta_colors, [colorKey]: value }
    }));
  };

  // iter120 — Eyedropper handler. Pulls the dominant brand color out of the
  // dealer's logo and writes it into the given color field. Only meaningful
  // for hex fields (we skip on `isColor: false` fields like overlay_bg
  // since those need alpha and a single solid color wouldn't help).
  const handleUseLogoColor = async (colorKey) => {
    const logoUrl = unlockSettings?.logo_url;
    if (!logoUrl) {
      showError("This dealer hasn't uploaded a logo yet.");
      return;
    }
    setPickingField(colorKey);
    try {
      const resp = await apiFetch(`/cta/dominant-color?url=${encodeURIComponent(logoUrl)}`, { method: "GET" });
      const hex = resp?.data?.hex || resp?.hex;
      if (!hex) throw new Error("No color returned from server");
      handleColorChange(colorKey, hex);
      showToast(`Matched ${hex} from logo`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Couldn't sample a color from the logo";
      showError(msg);
    } finally {
      setPickingField(null);
    }
  };

  const handleTemplateSelect = (templateId) => {
    if (disabled) return;
    
    const template = CTA_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setSettings(prev => {
        // iter127 — bug fix: previously every template click stomped the
        // dealer's saved button_text with the new template's defaultButton,
        // which is why dealers saw "Unlock Best Price" instead of their
        // own "Reveal Your Price" on page load (the load → effect chain
        // re-triggered a template select). Now we only seed button_text
        // when the current value is empty OR matches the OUTGOING
        // template's defaultButton — i.e. the dealer hasn't customised
        // it yet relative to the template they were just on.
        //
        // iter128 — RCA fix: previously compared against the full set of
        // ALL templates' defaultButtons via knownDefaults.includes(...).
        // That falsely classified a deliberate dealer choice like
        // "Reveal Your Price" as "untouched" because it happens to equal
        // the Blurred Price Reveal template's default. Now we compare only
        // against the OUTGOING template's default, so any value that
        // wasn't the outgoing template's default is preserved.
        const outgoingTemplate = CTA_TEMPLATES.find(t => t.id === prev.cta_template_id);
        const outgoingDefault = outgoingTemplate?.defaultButton;
        const isUntouched = !prev.button_text || prev.button_text === outgoingDefault;
        return {
          ...prev,
          cta_template_id: templateId,
          cta_colors: { ...prev.cta_colors, ...template.defaultColors },
          button_text: isUntouched ? template.defaultButton : prev.button_text,
        };
      });
    }
  };

  const handleScrapeColors = async () => {
    if (!dealerWebsite.trim()) {
      setScrapeMessage("Enter a website URL first.");
      return;
    }
    setScraping(true);
    setScrapedColors([]);
    setScrapeMessage("");
    try {
      const res = await apiFetch("/brand/scrape-colors", {
        method: "POST",
        data: { url: dealerWebsite.trim() },
      });
      const colors = res.data?.colors || [];
      setScrapedColors(colors);
      setScrapeMessage(res.data?.message || (colors.length ? "" : "Could not detect colors automatically — please enter hex values manually."));
      // Persist URL + palette on a successful scrape so the swatches reappear on reload
      if (typeof window !== "undefined") {
        if (websiteStorageKey) window.localStorage.setItem(websiteStorageKey, dealerWebsite.trim());
        if (colorsStorageKey) window.localStorage.setItem(colorsStorageKey, JSON.stringify(colors));
      }
    } catch (e) {
      setScrapeMessage(e.response?.data?.detail || "Could not detect colors automatically — please enter hex values manually.");
    } finally {
      setScraping(false);
    }
  };

  const handleSave = async () => {
    if (disabled) {
      showError("Cannot edit settings for a disabled dealer");
      return;
    }
    
    // Validate site adapter JSON if provided
    let siteAdapterValue = null;
    if (siteAdapterJson && siteAdapterJson.trim()) {
      try {
        siteAdapterValue = JSON.parse(siteAdapterJson);
      } catch (e) {
        showError("Invalid Site Adapter JSON format");
        return;
      }
    }
    
    // Generate trace ID for this save operation
    const traceId = `FE-SAVE-${Date.now()}`;
    
    // Build payload
    const payload = {
      enabled: settings.enabled,
      injection_mode: settings.injection_mode,
      site_adapter: siteAdapterValue ? JSON.stringify(siteAdapterValue) : null,
      cta_template_id: settings.cta_template_id,
      cta_script_text_key: settings.cta_script_text_key || null,
      cta_custom_headline_text: settings.cta_custom_headline_text || null,  // iter125
      cta_headline_scale: settings.cta_headline_scale,
      cta_colors: JSON.stringify(settings.cta_colors),
      button_text: settings.button_text,
      subtext: settings.subtext,
      reveal_content: settings.reveal_content,
      unlock_price: settings.unlock_price || null,
      require_sms_verification: settings.require_sms_verification,
      require_email: settings.require_email,
      require_first_name: settings.require_first_name,
      require_last_name: settings.require_last_name,
      // iter69 — unlock persistence
      unlock_persist_days: settings.unlock_persist_days,
      // Unlock notification recipients (normalize string → array on save)
      unlock_notify_emails: (typeof settings.unlock_notify_emails === "string")
        ? settings.unlock_notify_emails.split(/[\s,;\n]+/).map(e => e.trim()).filter(Boolean)
        : (settings.unlock_notify_emails || []),
      // Dealer Permission
      dealer_unlock_control_enabled: settings.dealer_unlock_control_enabled,
      // Also save legacy fields for backwards compatibility
      primary_color: settings.cta_colors.btn_bg,
      button_color: settings.cta_colors.btn_bg,
      text_color: settings.cta_colors.btn_text,
      accent_color: settings.cta_colors.headline,
    };
    
    try {
      setSaving(true);
      
      // STEP 1: Save
      await apiFetch(unlockSettingsApiPath, {
        method: "PUT",
        data: payload,
      });
      
      // STEP 2: Read-after-write verification
      const verifyResponse = await apiFetch(verifyDealerApiPath, {
        method: "GET",
      });
      
      const verifyUnlockPrice = isDealerSelf
        ? verifyResponse.data?.unlock_price
        : verifyResponse.data?.unlock_settings?.unlock_price;
      
      // Surface a non-blocking warning if the persisted value disagrees with
      // what we sent — this used to be loud debug logging in the console.
      if (verifyUnlockPrice !== payload.unlock_price) {
        showError("Save succeeded but a verification mismatch was detected. Please reload to confirm.");
      } else {
        showToast("Unlock settings saved successfully");
      }
      if (onRefresh) onRefresh();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[${traceId}] SAVE FAILED:`, err?.response?.data || err?.message);
      showError(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Render template preview thumbnail
  const renderTemplateThumbnail = (templateId) => {
    // Use the template's DEFAULT colors for thumbnails, not the current settings
    // This keeps each thumbnail looking like its own template preview
    const template = CTA_TEMPLATES.find(t => t.id === templateId);
    const defaultColors = template?.defaultColors || {};
    
    switch (templateId) {
      case "script_yellow_arrow":
        return <SvgThumbnailScriptYellowArrow colors={defaultColors} />;
      case "start_here_swoosh":
        return <SvgThumbnailStartHereSwoosh colors={defaultColors} />;
      case "start_here_box":
        return <SvgThumbnailStartHereBox colors={defaultColors} />;
      case "wow_price_arrow":
        return <SvgThumbnailWowPriceArrow colors={defaultColors} />;
      case "best_price_arrow":
        return <SvgThumbnailBestPriceArrow colors={defaultColors} />;
      case "simple_box":
        return <SvgThumbnailSimpleBox colors={defaultColors} />;
      case "button_rounded":
        return <SvgThumbnailButtonRounded colors={defaultColors} />;
      case "button_squared":
        return <SvgThumbnailButtonSquared colors={defaultColors} />;
      case "frosted_glass":
        return <SvgThumbnailFrostedGlass colors={defaultColors} />;
      case "urgency_strip":
        return <SvgThumbnailUrgencyStrip colors={defaultColors} />;
      case "split_badge":
        return <SvgThumbnailSplitBadge colors={defaultColors} />;
      case "neon_glow":
        return <SvgThumbnailNeonGlow colors={defaultColors} />;
      case "minimal_float":
        return <SvgThumbnailMinimalFloat colors={defaultColors} />;
      case "blurred_price_reveal":
        return <SvgThumbnailBlurredPriceReveal colors={defaultColors} />;
      case "live_activity":
        return <SvgThumbnailLiveActivity colors={defaultColors} />;
      case "savings_first":
        return <SvgThumbnailSavingsFirst colors={defaultColors} />;
      default:
        return null;
    }
  };

  // Render full CTA preview (matching embed.js output exactly)
  const renderCtaPreview = () => {
    const templateId = settings.cta_template_id || "script_yellow_arrow";
    const colors = settings.cta_colors;
    // iter127 — strip leading lock emoji so the inline <LockIcon> in
    // simple_box / button_rounded / button_squared previews doesn't
    // pair up with a typed 🔒 producing a double-lock.
    const rawButtonText = settings.button_text || "Unlock Instant Price";
    const buttonText = rawButtonText.replace(/^(?:\u{1F512}|\u{1F513}|\uFE0F|\s)+/u, "");
    
    // Get headline text from script text key (additive feature).
    // iter125 — if the dealer picked "__CUSTOM__", honor their free-text
    // override instead of looking up a preset label.
    const getHeadlineText = () => {
      if (settings.cta_script_text_key === "__CUSTOM__") {
        const trimmed = (settings.cta_custom_headline_text || "").trim();
        if (trimmed) return trimmed;
        return "Your custom headline"; // preview placeholder until they type
      }
      if (settings.cta_script_text_key) {
        const option = SCRIPT_TEXT_OPTIONS.find(o => o.key === settings.cta_script_text_key);
        if (option && option.key && option.key !== "__CUSTOM__") return option.label;
      }
      // Return template default
      return "Get Our Best Price!";
    };
    
    const headlineText = getHeadlineText();
    
    // Adjust font size based on text length
    let fontSize = 38;
    if (headlineText.length > 22) fontSize = 32;
    else if (headlineText.length > 18) fontSize = 35;

    // SVG Callout based on template
    const headlineScale = settings.cta_headline_scale || 1.0;
    
    const renderSvgCallout = () => {
      switch (templateId) {
        case "script_yellow_arrow":
          return (
            <svg className="w-full h-auto mb-2" viewBox="0 0 360 80" preserveAspectRatio="xMidYMid meet">
              {/* Yellow curved arrow */}
              <g className="animate-pulse">
                <path 
                  d="M45 15 C30 18, 20 35, 15 55 L22 48 M15 55 L8 46" 
                  fill="none" 
                  stroke={colors.arrow || "#facc15"} 
                  strokeWidth="6" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </g>
              {/* Script headline - uses selected text with scale transform */}
              <g transform={`translate(200, 50) scale(${headlineScale}) translate(-200, -50)`}>
                <text 
                  x="200" 
                  y="50" 
                  textAnchor="middle" 
                  fontSize={fontSize} 
                  fontStyle="italic" 
                  fill={colors.headline || "#2563eb"}
                  fontFamily="Brush Script MT, cursive"
                  className="animate-pulse"
                >
                  {headlineText}
                </text>
              </g>
            </svg>
          );
        
        case "start_here_swoosh":
          return (
            <svg className="w-full h-auto mb-2" viewBox="0 0 360 90" preserveAspectRatio="xMidYMid meet">
              {/* Script headline with scale transform */}
              <g transform={`translate(180, 28) scale(${headlineScale}) translate(-180, -28)`}>
                <text 
                  x="180" 
                  y="28" 
                  textAnchor="middle" 
                  fontSize="26" 
                  fontStyle="italic" 
                  fill={colors.headline || "#e11d48"}
                  fontFamily="Brush Script MT, cursive"
                >
                  {headlineText}
                </text>
              </g>
              {/* Bold START HERE */}
              <text 
                x="150" 
                y="68" 
                textAnchor="middle" 
                fontSize="32" 
                fontWeight="900" 
                fill={colors.subhead || "#111827"}
                fontFamily="Arial Black, sans-serif"
              >
                START HERE!
              </text>
              {/* Red swoosh arrow */}
              <g className="animate-bounce">
                <path 
                  d="M290 35 Q320 40, 330 55 Q335 65, 328 78 M320 68 L328 78 L338 70" 
                  fill="none" 
                  stroke={colors.arrow || "#e11d48"} 
                  strokeWidth="5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          );
        
        case "start_here_box":
          return (
            <svg className="w-full h-auto mb-2" viewBox="0 0 360 85" preserveAspectRatio="xMidYMid meet">
              {/* Script headline with scale transform */}
              <g transform={`translate(180, 26) scale(${headlineScale}) translate(-180, -26)`}>
                <text 
                  x="180" 
                  y="26" 
                  textAnchor="middle" 
                  fontSize="24" 
                  fontStyle="italic" 
                  fill={colors.headline || "#16a34a"}
                  fontFamily="Brush Script MT, cursive"
                >
                  {headlineText}
                </text>
              </g>
              {/* Bold START HERE */}
              <text 
                x="160" 
                y="62" 
                textAnchor="middle" 
                fontSize="28" 
                fontWeight="900" 
                fill={colors.subhead || "#111827"}
                fontFamily="Arial Black, sans-serif"
              >
                START HERE!
              </text>
              {/* Down arrow */}
              <g className="animate-bounce">
                <path 
                  d="M280 48 L280 72 M272 64 L280 72 L288 64" 
                  fill="none" 
                  stroke={colors.arrow || "#16a34a"} 
                  strokeWidth="4" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          );
        
        case "wow_price_arrow":
          return (
            <svg className="w-full h-auto mb-2" viewBox="0 0 360 130" preserveAspectRatio="xMidYMid meet">
              {/* Define proportional arrowhead marker (scales with stroke width) */}
              <defs>
                <marker 
                  id="vdArrowHead-preview" 
                  markerUnits="strokeWidth"
                  markerWidth="2.2" 
                  markerHeight="2.2" 
                  refX="2.0" 
                  refY="1.1" 
                  orient="auto"
                >
                  <path d="M0,0 L2.2,1.1 L0,2.2 Z" fill={colors.arrow || "#111827"} />
                </marker>
              </defs>
              {/* Top question with scale transform */}
              <g transform={`translate(180, 24) scale(${headlineScale}) translate(-180, -24)`}>
                <text 
                  x="180" 
                  y="24" 
                  textAnchor="middle" 
                  fontSize="20" 
                  fontStyle="italic" 
                  fill={colors.headline || "#374151"}
                  fontFamily="Arial, sans-serif"
                >
                  {headlineText}
                </text>
              </g>
              {/* GET text */}
              <text 
                x="100" 
                y="58" 
                textAnchor="middle" 
                fontSize="28" 
                fontWeight="900" 
                fill={colors.subhead || "#dc2626"}
                fontFamily="Arial Black, sans-serif"
              >
                GET
              </text>
              {/* WOW! text - largest */}
              <text 
                x="140" 
                y="98" 
                textAnchor="middle" 
                fontSize="48" 
                fontWeight="900" 
                fill={colors.subhead || "#dc2626"}
                fontFamily="Arial Black, sans-serif"
                className="animate-pulse"
              >
                WOW!
              </text>
              {/* PRICE text */}
              <text 
                x="180" 
                y="122" 
                textAnchor="middle" 
                fontSize="24" 
                fontWeight="900" 
                fill={colors.subhead || "#dc2626"}
                fontFamily="Arial Black, sans-serif"
              >
                PRICE
              </text>
              {/* Curved arrow with proportional arrowhead */}
              <g className="animate-bounce">
                <path 
                  d="M260 40 C300 45, 320 70, 310 105" 
                  fill="none" 
                  stroke={colors.arrow || "#111827"} 
                  strokeWidth="6" 
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  markerEnd="url(#vdArrowHead-preview)"
                />
              </g>
            </svg>
          );
        
        case "best_price_arrow":
          return (
            <svg className="w-full h-auto mb-2" viewBox="0 0 280 100" preserveAspectRatio="xMidYMid meet">
              {/* Main text "GET OUR BEST PRICE!" - bold red */}
              <g className="animate-pulse">
                <text 
                  x="140" 
                  y="30" 
                  textAnchor="middle" 
                  fontSize="24" 
                  fontWeight="900" 
                  fill={colors.subhead || "#dc2626"}
                  fontFamily="Arial Black, Impact, sans-serif"
                >
                  GET OUR
                </text>
                <text 
                  x="140" 
                  y="58" 
                  textAnchor="middle" 
                  fontSize="24" 
                  fontWeight="900" 
                  fill={colors.subhead || "#dc2626"}
                  fontFamily="Arial Black, Impact, sans-serif"
                >
                  BEST PRICE!
                </text>
              </g>
              {/* Down arrow */}
              <g className="animate-bounce">
                <path 
                  d="M140 65 L140 90 M125 80 L140 90 L155 80" 
                  fill="none" 
                  stroke={colors.arrow || "#dc2626"} 
                  strokeWidth="5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          );
        
        case "simple_box":
          // No callout for simple_box - just the button
          return null;
        
        case "button_rounded":
        case "button_squared":
          // No callout for button-only templates
          return null;
        
        default:
          return null;
      }
    };

    // Button styles - varies by template
    const getButtonStyle = () => {
      const base = {
        backgroundColor: colors.btn_bg || "#16a34a",
        color: colors.btn_text || "#ffffff",
      };
      
      switch (templateId) {
        case "start_here_box":
          return { ...base, borderRadius: "6px", border: `2px solid ${colors.subhead || "#111827"}` };
        case "wow_price_arrow":
          return { ...base, borderRadius: "8px" };
        case "best_price_arrow":
          return { ...base, borderRadius: "8px", marginTop: "8px" };
        case "simple_box":
          return { ...base, borderRadius: "8px", padding: "14px 24px", fontSize: "16px", fontWeight: "600", minWidth: "220px" };
        case "button_rounded":
          return { ...base, borderRadius: "50px", padding: "16px 32px", fontSize: "17px" };
        case "button_squared":
          return { ...base, borderRadius: "6px", padding: "16px 32px", fontSize: "17px" };
        default:
          return { ...base, borderRadius: "25px" };
      }
    };
    
    const buttonStyle = getButtonStyle();
    
    // Lock badge for button-only templates
    const isButtonOnly = templateId === "button_rounded" || templateId === "button_squared";

    // ============== Animated premium templates (v2) ==============
    // These render a custom HTML structure (not SVG-callout + standalone button).
    const animatedTemplateIds = [
      "frosted_glass", "urgency_strip", "split_badge", "neon_glow", "minimal_float",
      "blurred_price_reveal", "live_activity", "savings_first",  // iter123
    ];
    if (animatedTemplateIds.includes(templateId)) {
      return (
        <div className="w-full max-w-[360px] flex flex-col items-center" data-testid="cta-preview-animated">
          <AnimatedCtaPreview
            templateId={templateId}
            colors={colors}
            buttonText={buttonText}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center w-full max-w-[360px]">
        {renderSvgCallout()}
        <button
          className="w-full py-3.5 px-6 font-bold flex items-center justify-center gap-2 transition-all hover:shadow-lg shadow-md"
          style={buttonStyle}
          data-testid="cta-preview-button"
        >
          {isButtonOnly ? (
            // Badge-style lock icon for button-only templates
            <span 
              className="flex items-center justify-center w-8 h-8 rounded-full mr-1"
              style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}
            >
              <LockIcon className="w-4 h-4" color={colors.btn_bg || "#16a34a"} />
            </span>
          ) : (
            // Standard inline lock icon
            <LockIcon className="w-4 h-4" color={colors.btn_icon || colors.btn_text || "#ffffff"} />
          )}
          {buttonText}
        </button>
      </div>
    );
  };

  const selectedTemplate = CTA_TEMPLATES.find(t => t.id === settings.cta_template_id) || CTA_TEMPLATES[0];

  // iter127 — bug fix: dealers who paste a 🔒 emoji into their button copy
  // got a double-lock in the preview because templates also render an inline
  // <LockIcon>. Strip leading lock-family emoji + whitespace from the
  // displayed text. We do NOT mutate the saved setting.
  const stripLeadingLockEmoji = (text) => {
    if (!text) return text;
    return text.replace(/^(?:\u{1F512}|\u{1F513}|\uFE0F|\s)+/u, "");
  };
  const buttonTextForPreview = stripLeadingLockEmoji(settings.button_text);

  // Get color fields to show based on template
  const getColorFields = () => {
    const baseFields = [
      { key: "btn_bg", label: "Button Background", description: "Main button color" },
      { key: "btn_text", label: "Button Text", description: "Text and icon color on button" },
    ];
    
    const templateFields = {
      script_yellow_arrow: [
        { key: "headline", label: "Script Headline", description: '"Get Our Best Price!" text color' },
        { key: "arrow", label: "Yellow Arrow", description: "Curved arrow color (default yellow)" },
      ],
      start_here_swoosh: [
        { key: "headline", label: "Script Question", description: '"Want a Lower Price?" text color' },
        { key: "subhead", label: "START HERE!", description: "Bold text color (usually black)" },
        { key: "arrow", label: "Swoosh Arrow", description: "Red swoosh arrow color" },
      ],
      start_here_box: [
        { key: "headline", label: "Script Question", description: '"Want a Lower Price?" text color' },
        { key: "subhead", label: "START HERE!", description: "Bold text color (usually black)" },
        { key: "arrow", label: "Down Arrow", description: "Arrow pointing to button" },
      ],
      wow_price_arrow: [
        { key: "headline", label: "Question Text", description: '"Want a Lower Price?" gray text' },
        { key: "subhead", label: "GET WOW! PRICE", description: "Red bold text color" },
        { key: "arrow", label: "Curved Arrow", description: "Black curved arrow" },
      ],
      best_price_arrow: [
        { key: "headline", label: "Script Headline", description: '"Want Our Best Price?" text color' },
        { key: "arrow", label: "Curved Arrow", description: "Arrow pointing to button color" },
      ],
      simple_box: [
        { key: "headline", label: "Box Headline", description: "Headline text color inside the box" },
        { key: "box_bg", label: "Box Background", description: "Background color of the callout box" },
      ],
      // Button-only templates have no additional color fields (just btn_bg and btn_text)
      button_rounded: [],
      button_squared: [],
      // ============== Animated premium templates (v2) ==============
      frosted_glass: [
        { key: "frame_bg", label: "Frame / Border Color", description: "Outer gradient frame around the frosted card (the part you see at the edges). Defaults to a blue→purple gradient.", isColor: true },
        { key: "overlay_bg", label: "Card Background", description: "Translucent frosted surface inside the frame — the area the shimmer animation sweeps across. Use an rgba() value (e.g. rgba(255,255,255,0.65)) to keep some transparency.", isColor: false },
        { key: "headline", label: "Overlay Headline", description: '"EXCLUSIVE PRICE" text on the frosted card' },
      ],
      urgency_strip: [
        { key: "banner_bg", label: "Banner Background", description: "Full-width strip background (default near-black)" },
        { key: "headline", label: "Banner Text", description: "Color of the urgency message text" },
        { key: "dot", label: "Pulsing Dot", description: "Live-recording-style indicator color (default red)" },
      ],
      split_badge: [
        { key: "badge_bg", label: "Badge Background", description: 'Left side ("EXCLUSIVE PRICE") panel color' },
        { key: "badge_text", label: "Badge Text & Lock", description: "Color of the locked-icon and label on the left side" },
      ],
      neon_glow: [
        { key: "glow", label: "Neon Glow Color", description: "Pulsing border + ambient glow color" },
      ],
      minimal_float: [],
      // iter123 — new template color fields
      blurred_price_reveal: [
        { key: "headline", label: "Heading Text", description: "Color of MSRP label + 'Your Price' text" },
        { key: "accent", label: "Blurred Price Color", description: "Color of the blurred '$XX,XXX' (revealed on click)" },
        { key: "card_bg", label: "Card Background", description: "Background fill of the card (defaults to white)" },
        { key: "muted", label: "Subtext / MSRP Color", description: "Muted color for the secondary 'MSRP' line" },
      ],
      live_activity: [
        { key: "muted", label: "Activity Line Color", description: "Color of the '7 shoppers viewing' text" },
        { key: "dot", label: "Pulsing Dot", description: "Live-recording-style indicator color (default red)" },
      ],
      savings_first: [
        { key: "headline", label: "Headline Text", description: "Color of 'You could save … below MSRP' copy" },
        { key: "accent", label: "Savings Amount Color", description: "Color of the '$3,200' savings number + card border" },
        { key: "card_bg", label: "Card Background", description: "Background fill of the savings card (defaults to soft mint)" },
      ],
    };
    
    return [...baseFields, ...(templateFields[settings.cta_template_id] || [])];
  };

  return (
    <div
      className="flex flex-col-reverse gap-6 lg:flex-row lg:items-start"
      data-testid="branding-tab-layout"
    >
      {/* Settings Panel */}
      <div className="space-y-6 lg:flex-1 lg:min-w-0">
        {/* iter86 — Copy branding from another dealer. Compact action
            card at the top of the Branding tab so master admins can
            clone a "golden" rooftop's CTA setup onto a freshly
            onboarded dealer in one click. Modifies ONLY this dealer
            (the one currently being viewed). */}
        {!isDealerSelf && (
        <Card data-testid="copy-branding-card">
          <CardContent className="py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 m-0">Clone branding from another dealer</p>
              <p className="text-xs text-gray-500 m-0 mt-0.5">
                Copy CTA template, colors, button text, headline, reveal content &amp; logo from one of your other dealers onto <span className="font-medium">{dealer?.name}</span>. Pricing rules and permissions stay untouched.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={openCopyModal}
              disabled={disabled}
              data-testid="open-copy-branding-modal-btn"
              className="whitespace-nowrap"
            >
              Copy from dealer…
            </Button>
          </CardContent>
        </Card>
        )}

        {/* Dealer Website + Brand Color Scraper (optional helper) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Dealer Website</CardTitle>
            <CardDescription>
              Paste your dealership&apos;s website URL and click &quot;Scrape Brand Colors&quot; to auto-suggest button colors. This is an optional helper — you can always enter hex values manually.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={dealerWebsite}
                onChange={(e) => setDealerWebsite(e.target.value)}
                placeholder="https://yourdealership.com"
                disabled={disabled}
                className="flex-1"
                data-testid="dealer-website-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleScrapeColors}
                disabled={disabled || scraping || !dealerWebsite.trim()}
                data-testid="scrape-brand-colors-btn"
              >
                {scraping ? "Scraping…" : "Scrape Brand Colors"}
              </Button>
            </div>
            {scrapedColors.length > 0 && (
              <div className="space-y-1.5" data-testid="scraped-colors-swatches">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-gray-600">Click a swatch to apply it to the Button Background color:</p>
                  <button
                    type="button"
                    onClick={handleScrapeColors}
                    disabled={disabled || scraping || !dealerWebsite.trim()}
                    className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={
                      !dealerWebsite.trim()
                        ? "Enter a website URL first"
                        : "Re-scrape brand colors from the saved URL"
                    }
                    data-testid="refresh-brand-colors-btn"
                  >
                    <RefreshCw size={12} className={scraping ? "animate-spin" : ""} />
                    {scraping ? "Refreshing…" : "Refresh"}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {scrapedColors.map((hex) => (
                    <button
                      key={hex}
                      type="button"
                      onClick={() => { handleColorChange("btn_bg", hex); }}
                      className="h-9 rounded-md border border-gray-200 shadow-sm flex items-center gap-2 px-2 hover:ring-2 hover:ring-offset-1 hover:ring-purple-400 transition-all"
                      style={{ cursor: "pointer" }}
                      title={`Apply ${hex} to Button Background`}
                      data-testid={`scraped-color-${hex.slice(1)}`}
                    >
                      <span
                        className="inline-block h-5 w-5 rounded border border-gray-300"
                        style={{ backgroundColor: hex }}
                      />
                      <span className="text-xs font-mono text-gray-700">{hex}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {scrapeMessage && scrapedColors.length === 0 && (
              <p className="text-xs text-amber-700" data-testid="scrape-message">{scrapeMessage}</p>
            )}
          </CardContent>
        </Card>

        {/* Feature Toggle */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Unlock Feature</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* iter127 — Hidden in dealer-self view: the Master "Enable
                Unlock Feature" toggle at the top of UnlockSettingsPage
                already controls this exact `settings.enabled` flag.
                Master admins still see this control on the /admin
                dealer-detail tab where the top-level toggle isn't shown. */}
            {!isDealerSelf && (
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Unlock Offer</Label>
                <p className="text-sm text-gray-500">Show unlock CTAs on dealer website</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => handleChange("enabled", checked)}
                disabled={disabled}
                data-testid="unlock-enabled-toggle"
              />
            </div>
            )}
            
            {/* Dealer Permission Toggle — admin-only */}
            {!isDealerSelf && (
            <div className="pt-3 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Dealer Unlock Control</Label>
                  <p className="text-sm text-gray-500">
                    Allow dealer to see and edit Unlock Offer in their dashboard
                  </p>
                </div>
                <Switch
                  checked={settings.dealer_unlock_control_enabled}
                  onCheckedChange={(checked) => handleChange("dealer_unlock_control_enabled", checked)}
                  disabled={disabled}
                  data-testid="dealer-unlock-control-toggle"
                />
              </div>
              {!settings.dealer_unlock_control_enabled && (
                <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                  ⚠️ Dealer cannot see &quot;Unlock Offer&quot; in their sidebar when this is disabled
                </p>
              )}
            </div>
            )}
          </CardContent>
        </Card>

        {/* Injection Mode */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              ⚡ Injection Mode
            </CardTitle>
            <CardDescription>
              How CTAs are placed on the dealer&apos;s website
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div
                onClick={() => !disabled && handleChange("injection_mode", "auto")}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  settings.injection_mode === "auto"
                    ? "border-green-500 bg-green-50"
                    : "border-gray-200 hover:border-gray-300"
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="injection-mode-auto"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">🚀</span>
                  <span className="font-semibold text-green-700">Turnkey Auto</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Recommended</span>
                </div>
                <p className="text-sm text-gray-600">
                  CTAs are automatically injected on inventory (SRP) and vehicle detail (VDP) pages. 
                  No HTML changes needed by the dealer.
                </p>
              </div>
              
              <div
                onClick={() => !disabled && handleChange("injection_mode", "manual")}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  settings.injection_mode === "manual"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="injection-mode-manual"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">🔧</span>
                  <span className="font-semibold text-blue-700">Manual</span>
                </div>
                <p className="text-sm text-gray-600">
                  Dealer must add <code className="bg-gray-100 px-1 rounded text-xs">data-vd-unlock=&quot;VIN&quot;</code> attributes 
                  to their HTML. Best for custom implementations.
                </p>
              </div>
            </div>
            
            {/* Site Adapter (Advanced) - only show in auto mode */}
            {settings.injection_mode === "auto" && (
              <div className="pt-4 border-t">
                <button 
                  onClick={() => setShowSiteAdapter(!showSiteAdapter)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  {showSiteAdapter ? "▼" : "▶"} Advanced: Site Adapter Selectors
                </button>
                
                {showSiteAdapter && (
                  <div className="mt-3 space-y-3">
                    <p className="text-xs text-gray-500">
                      Custom CSS selectors for auto-injection. Leave empty for heuristic detection.
                    </p>
                    <Textarea
                      value={siteAdapterJson}
                      onChange={(e) => setSiteAdapterJson(e.target.value)}
                      placeholder={`{
  "srp_vehicle_card_selector": ".vehicle-card",
  "srp_price_selector": ".price",
  "srp_cta_container_selector": ".cta-area",
  "vdp_price_selector": ".vehicle-price",
  "vdp_cta_container_selector": ".vdp-cta"
}`}
                      rows={8}
                      className="font-mono text-xs"
                      disabled={disabled}
                      data-testid="site-adapter-json"
                    />
                    <p className="text-xs text-amber-600">
                      ⚠️ Only modify if you understand the dealer&apos;s website structure. Invalid selectors may break CTA placement.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA Template Selector */}
        <Card id="vd-cta-template-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              🎨 CTA Template
            </CardTitle>
            <CardDescription>
              Choose a visual style for the unlock CTA (SVG artwork)
            </CardDescription>
            {/* iter127 — Quick "Jump to Live Preview" link until the full
                sticky two-column layout lands. Smooth-scrolls to the
                preview card lower on the page. */}
            <a
              href="#vd-live-preview"
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 mt-1"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("vd-live-preview")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="jump-to-preview-link"
            >
              See Live Preview ↓
            </a>
          </CardHeader>
          <CardContent className="space-y-3">
            {CTA_TEMPLATES.map((template) => (
              <div
                key={template.id}
                onClick={() => handleTemplateSelect(template.id)}
                className={`group relative flex items-center gap-4 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  settings.cta_template_id === template.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid={`template-${template.id}`}
              >
                {/* SVG Thumbnail */}
                <div className="w-24 h-14 flex items-center justify-center bg-white rounded border overflow-hidden">
                  {renderTemplateThumbnail(template.id)}
                </div>
                {/* Info */}
                <div className="flex-1">
                  <div className="font-semibold text-sm">{template.name}</div>
                  <div className="text-xs text-gray-500">{template.description}</div>
                </div>
                {settings.cta_template_id === template.id && (
                  <span className="text-blue-500 text-lg">✓</span>
                )}
                {/* iter128 — UX Item 7: enlarged hover preview popover.
                    Pure CSS via `group-hover`. Positioned above-right of the
                    thumbnail so it never overlaps the sticky Live Preview
                    panel on the right. Pointer-events-none so it can't
                    intercept clicks. */}
                <div
                  className="pointer-events-none absolute left-0 bottom-full mb-2 z-30 hidden group-hover:block w-[220px] p-3 bg-white border border-gray-200 rounded-lg shadow-xl"
                  data-testid={`template-hover-preview-${template.id}`}
                >
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Enlarged Preview</div>
                  <div className="w-full h-[100px] flex items-center justify-center bg-gray-50 rounded border overflow-hidden">
                    <div style={{ transform: "scale(2)", transformOrigin: "center" }}>
                      {renderTemplateThumbnail(template.id)}
                    </div>
                  </div>
                  <div className="mt-2 font-semibold text-sm text-gray-900">{template.name}</div>
                  <div className="text-xs text-gray-500 leading-snug">{template.description}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Headline Size & Text — only for headline-capable templates */}
        {(() => {
          // iter116 — single source of truth for which templates show a
          // text headline above the button. Was previously a hardcoded
          // check in two places; centralising prevents drift.
          //
          // iter125 — Trimmed to only the four templates whose backend SVG
          // factory actually consumes `cta_script_text_key`. Previously
          // `simple_box`, `frosted_glass`, `urgency_strip`, and
          // `best_price_arrow` lit up this card too, but none of them wire
          // the dropdown to a rendered element — the picker did nothing for
          // dealers on those templates. Now those templates fall into the
          // "Headline customization not applicable" copy branch.
          const HEADLINE_TEMPLATES = [
            "script_yellow_arrow", "start_here_swoosh", "start_here_box",
            "wow_price_arrow",
          ];
          const BUTTON_ONLY = ["button_rounded", "button_squared", "neon_glow", "minimal_float", "split_badge"];
          const tmpl = settings.cta_template_id;
          const supportsHeadline = HEADLINE_TEMPLATES.includes(tmpl);
          const buttonOnly = BUTTON_ONLY.includes(tmpl);
          if (buttonOnly) {
            // Render nothing — these templates have no headline / scale.
            return null;
          }
          return (
        <Card data-testid="branding-headline-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Headline Text & Size</CardTitle>
            <CardDescription>
              {supportsHeadline
                ? "Pick a headline preset, then adjust its size to taste."
                : "Headline customization is not applicable to the selected template."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Headline Text dropdown — for ALL headline-capable templates */}
            {supportsHeadline && (
              <div className="space-y-2 mb-4">
                <Label>Headline Text</Label>
                <select
                  value={settings.cta_script_text_key || ""}
                  onChange={(e) => handleChange("cta_script_text_key", e.target.value || null)}
                  className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={disabled}
                  data-testid="script-text-select"
                >
                  {SCRIPT_TEXT_OPTIONS.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {/* iter125 — Free-text input appears only when Custom is chosen */}
                {settings.cta_script_text_key === "__CUSTOM__" && (
                  <div className="mt-2 space-y-1">
                    <Input
                      type="text"
                      maxLength={120}
                      value={settings.cta_custom_headline_text || ""}
                      onChange={(e) => handleChange("cta_custom_headline_text", e.target.value)}
                      placeholder="Type your headline (max 120 chars)"
                      disabled={disabled}
                      data-testid="custom-headline-input"
                    />
                    <p className="text-xs text-gray-500">
                      Visible above the button on the {SCRIPT_TEXT_OPTIONS.find(o => o.key === settings.cta_script_text_key)?.label || "selected"} template. Keep it short — the live preview updates as you type.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* iter125 — Friendly explainer for templates that don't render
                the dropdown text. We chose option B from the user spec:
                show the message instead of forcing a headline into a
                template that wasn't designed for one. */}
            {!supportsHeadline && (
              <div className="mb-4 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-sm" data-testid="headline-not-available">
                <strong className="block mb-1">Headline customization isn&apos;t available for this template.</strong>
                <span className="text-amber-800">
                  The selected template ships with its own design copy (e.g. the frosted &quot;EXCLUSIVE PRICE&quot; badge, the &quot;GET OUR / BEST PRICE!&quot; arrow, or a button-only layout). Pick a template like <em>Script Yellow Arrow</em>, <em>Start Here Swoosh / Box</em>, or <em>Wow Price Arrow</em> if you want to control the headline copy.
                </span>
              </div>
            )}

            {/* Headline Size slider */}
            <div className="space-y-2">
              <Label>Headline Size: {(settings.cta_headline_scale || 1.0).toFixed(2)}x</Label>
              <input
                type="range"
                min="0.8"
                max="1.3"
                step="0.05"
                value={settings.cta_headline_scale || 1.0}
                onChange={(e) => handleChange("cta_headline_scale", parseFloat(e.target.value))}
                className={`w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600 ${
                  !supportsHeadline ? "opacity-50" : ""
                }`}
                disabled={disabled || !supportsHeadline}
                data-testid="headline-scale-slider"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>Smaller (0.8x)</span>
                <span>Default (1.0x)</span>
                <span>Larger (1.3x)</span>
              </div>
            </div>
          </CardContent>
        </Card>
          );
        })()}

        {/* Button Text */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Button Text</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Button Label</Label>
              <Input
                value={settings.button_text || ""}
                onChange={(e) => handleChange("button_text", e.target.value)}
                placeholder={selectedTemplate?.defaultButton || "Unlock Instant Price"}
                disabled={disabled}
                data-testid="button-text-input"
              />
              <p className="text-xs text-gray-500">
                Your saved label. Placeholder shows the selected template&apos;s suggested default — clear the field to fall back to it.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Modal Subtext</Label>
              <Textarea
                value={settings.subtext || ''}
                onChange={(e) => handleChange("subtext", e.target.value)}
                placeholder="Enter your details to reveal..."
                rows={2}
                disabled={disabled}
              />
            </div>
          </CardContent>
        </Card>

        {/* Color Customization */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg">Color Customization</CardTitle>
                <CardDescription>
                  Customize colors for the selected template
                </CardDescription>
              </div>
              {/* iter85 — one-click reset to the selected template's default
                  color palette. Confirms before clobbering so a dealer
                  doesn't lose a long-tuned palette by accident. */}
              <button
                type="button"
                onClick={() => {
                  const template = CTA_TEMPLATES.find(t => t.id === settings.cta_template_id);
                  if (!template) {
                    showError("No template selected — pick a template first.");
                    return;
                  }
                  if (!window.confirm(
                    `Reset all colors to the "${template.name}" template defaults? ` +
                    `Your current color picks will be lost.`
                  )) return;
                  setSettings(prev => ({
                    ...prev,
                    cta_colors: { ...prev.cta_colors, ...template.defaultColors }
                  }));
                  showToast(`Colors reset to ${template.name} defaults — click Save Settings to persist.`);
                }}
                disabled={disabled || !settings.cta_template_id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                data-testid="reset-colors-to-defaults-btn"
                title="Restore the colors that ship with the currently selected CTA template"
              >
                <RefreshCw size={13} /> Reset to defaults
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {getColorFields().map(field => {
                // iter128 — UX Item 6: detect when a color field matches its
                // template-default value so we can show a soft "Default" pill
                // next to the swatch + a per-field reset icon when the dealer
                // has changed it. Helps dealers see what they've customised.
                const tmpl = CTA_TEMPLATES.find(t => t.id === settings.cta_template_id);
                const defaultVal = tmpl?.defaultColors?.[field.key];
                const currentVal = settings.cta_colors[field.key] || "";
                const isAtDefault = defaultVal != null &&
                  currentVal.toLowerCase() === defaultVal.toLowerCase();
                return (
                <div key={field.key} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Label>{field.label}</Label>
                      {isAtDefault && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200"
                          title="This color matches the template's default value"
                          data-testid={`default-badge-${field.key}`}
                        >
                          Default
                        </span>
                      )}
                      {!isAtDefault && defaultVal && (
                        <button
                          type="button"
                          onClick={() => handleColorChange(field.key, defaultVal)}
                          disabled={disabled}
                          title={`Reset to template default (${defaultVal})`}
                          data-testid={`reset-color-${field.key}`}
                          className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <RefreshCw size={10} /> Reset
                        </button>
                      )}
                    </div>
                    {/* iter120 — Eyedropper: only shown for hex color fields
                        when the dealer has a logo on file. Lets master admins
                        / dealers one-click match the field to the brand. */}
                    {field.isColor !== false && unlockSettings?.logo_url && (
                      <button
                        type="button"
                        onClick={() => handleUseLogoColor(field.key)}
                        disabled={disabled || pickingField === field.key}
                        title="Pick dominant color from dealer logo"
                        data-testid={`eyedropper-${field.key}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Pipette className={`w-3 h-3 ${pickingField === field.key ? "animate-pulse" : ""}`} />
                        {pickingField === field.key ? "Sampling…" : "Use brand color"}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {field.isColor !== false && (
                      <input
                        type="color"
                        value={(settings.cta_colors[field.key] && settings.cta_colors[field.key].startsWith("#") ? settings.cta_colors[field.key] : "#000000")}
                        onChange={(e) => handleColorChange(field.key, e.target.value)}
                        className="w-12 h-10 rounded border cursor-pointer"
                        disabled={disabled}
                        data-testid={`color-${field.key}`}
                      />
                    )}
                    <Input
                      value={settings.cta_colors[field.key] || ""}
                      onChange={(e) => handleColorChange(field.key, e.target.value)}
                      className="flex-1"
                      disabled={disabled}
                      placeholder={field.isColor === false ? "rgba(255,255,255,0.65)" : undefined}
                      data-testid={`color-text-${field.key}`}
                    />
                  </div>
                  <p className="text-xs text-gray-500">{field.description}</p>
                </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Form Requirements */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Form Requirements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>SMS Verification</Label>
                <p className="text-sm text-gray-500">Require phone verification</p>
              </div>
              <Switch
                checked={settings.require_sms_verification}
                onCheckedChange={(checked) => handleChange("require_sms_verification", checked)}
                disabled={disabled}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Email Required</Label>
              </div>
              <Switch
                checked={settings.require_email}
                onCheckedChange={(checked) => handleChange("require_email", checked)}
                disabled={disabled}
              />
            </div>
            
            {/* iter69 — Unlock Persistence dropdown.
                iter127 — Hidden in dealer-self view; the dealer's own
                UnlockSettingsPage already exposes the same dropdown in its
                Behavior section (one writes, one read — duplicate UI was
                confusing). Master admins still see this on the admin
                dealer-detail tab where it's the only place to control it. */}
            {!isDealerSelf && (
            <div className="space-y-1.5 pt-2 border-t border-gray-100">
              <Label htmlFor="unlock-persist-days-admin">Unlock Persistence</Label>
              <select
                id="unlock-persist-days-admin"
                data-testid="admin-unlock-persist-days-select"
                value={settings.unlock_persist_days ?? 30}
                onChange={(e) => handleChange("unlock_persist_days", parseInt(e.target.value))}
                disabled={disabled}
                className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>2 minutes (Demo)</option>
                <option value={1}>24 hours</option>
                <option value={3}>72 hours</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={21}>21 days</option>
                <option value={30}>30 days</option>
              </select>
              <p className="text-xs text-gray-500">
                How long an unlocked price stays valid for the same visitor before re-verification. <strong>Demo (2 min)</strong> is for testing the lock/unlock cycle.
              </p>
            </div>
            )}

            {/* Unlock notification recipients */}
            <div className="space-y-1.5 pt-2 border-t border-gray-100" data-testid="admin-unlock-notify-section">
              <Label htmlFor="unlock-notify-emails-admin">Notify on Unlock — Recipients</Label>
              <textarea
                id="unlock-notify-emails-admin"
                data-testid="admin-unlock-notify-emails-input"
                rows={3}
                placeholder="sales@dealer.com, gm@dealer.com"
                value={
                  Array.isArray(settings.unlock_notify_emails)
                    ? settings.unlock_notify_emails.join(", ")
                    : (settings.unlock_notify_emails || "")
                }
                onChange={(e) => handleChange("unlock_notify_emails", e.target.value)}
                disabled={disabled}
                className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500">
                Send an email to these recipients every time a visitor unlocks a price.
                Separate multiple addresses with commas, spaces, or new lines. Leave blank to disable.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving || disabled}
          className="w-full"
          data-testid="save-branding-btn"
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      {/* Preview Panel — sticky column. align-items: flex-start on the parent
           is critical: without it, this flex child would stretch to the full
           height of the settings column and sticky would have nowhere to stick. */}
      {/* iter101 — Preview restored on dealer side per user request. The card
          uses `lg:sticky lg:top-6` so as the dealer scrolls through CTA
          template + headline + button-text edits, the preview stays pinned
          in the viewport showing live changes. */}
      <div className="space-y-6 lg:w-[420px] lg:flex-shrink-0 lg:sticky lg:top-6 lg:self-start" id="vd-live-preview">
        <Card data-testid="branding-live-preview-card">
          <CardHeader>
            <CardTitle className="text-lg">Live Preview</CardTitle>
            <CardDescription>
              Preview at 360px width (as it will render via embed.js)
            </CardDescription>
            {/* iter127 — Back to Templates anchor */}
            <a
              href="#vd-cta-template-section"
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 mt-1"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("vd-cta-template-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="back-to-templates-link"
            >
              ↑ Back to Templates
            </a>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* CTA Preview */}
            <div>
              <Label className="text-xs text-gray-500 uppercase tracking-wider mb-3 block">
                CTA Preview (360px max-width)
              </Label>
              <div className="p-8 bg-gray-100 rounded-lg flex items-center justify-center min-h-[240px]">
                {renderCtaPreview()}
              </div>
            </div>

            {/* Template Info */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="font-semibold text-blue-900 mb-1">
                {selectedTemplate.name}
              </div>
              <div className="text-sm text-blue-700">
                {selectedTemplate.description}
              </div>
            </div>

            {/* Color Legend */}
            <div className="text-xs text-gray-500 space-y-2">
              <div className="font-medium text-gray-700 mb-1">Active Colors:</div>
              {getColorFields().map(field => {
                // iter128 — UX Item 8: each swatch is wrapped in a CSS-only
                // tooltip that surfaces the role + current hex/rgba value on
                // hover. Helps dealers verify exact brand colors without
                // scrolling back up to the color editor.
                const val = settings.cta_colors[field.key] || "#000";
                const display = String(val).toUpperCase();
                const tipText = `${field.label} — ${display}`;
                return (
                <div
                  key={field.key}
                  className="relative group flex items-center gap-2"
                  data-testid={`active-color-row-${field.key}`}
                >
                  <span
                    className="w-4 h-4 rounded border cursor-help"
                    style={{ backgroundColor: val }}
                    tabIndex={0}
                    aria-label={tipText}
                  />
                  <span>{field.label}</span>
                  <span className="ml-1 text-[10px] text-gray-400 uppercase tracking-wider">{display}</span>
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute left-0 -top-7 z-10 whitespace-nowrap px-2 py-1 rounded bg-gray-900 text-white text-[10px] font-medium opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                    data-testid={`active-color-tooltip-${field.key}`}
                  >
                    {tipText}
                  </span>
                </div>
                );
              })}
            </div>

            {/* Implementation Note */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800">
              <strong>Note:</strong> SVG artwork uses outlined text paths for exact visual fidelity across all browsers. Background is transparent to work on any dealer site.
            </div>
          </CardContent>
        </Card>

        {/* DOM Diagnostic — one-click "is the embed installed?" tool. */}
        <DomDiagnosticCard businessId={dealer?.id} />
      </div>

      {/* iter86 — Copy Branding modal. Lazy-loads the dealer list on
          first open, scopes the dropdown to non-archived dealers
          excluding the current one, and surfaces a hard confirm
          before clobbering the in-flight branding. */}
      {copyModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="copy-branding-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setCopyModalOpen(false); }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 m-0">Copy branding from another dealer</h3>
            <p className="text-sm text-gray-600 mt-1.5">
              Choose a dealer to copy from. The CTA template, colors, button text, headline, reveal content and logo will be cloned onto <strong>{dealer?.name}</strong>.
            </p>

            <div className="mt-4">
              <Label className="text-sm font-medium text-gray-700">Source dealer</Label>
              <select
                value={copySelectedSourceId}
                onChange={(e) => setCopySelectedSourceId(e.target.value)}
                disabled={copyDealersLoading || copyApplying}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                data-testid="copy-branding-source-select"
              >
                <option value="">
                  {copyDealersLoading ? "Loading dealers…" : "— Select a dealer —"}
                </option>
                {copyDealers.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              {!copyDealersLoading && copyDealers.length === 0 && (
                <p className="text-xs text-amber-700 mt-2">
                  No other dealers found to copy from.
                </p>
              )}
            </div>

            <div className="mt-3 p-3 bg-amber-50 rounded-md border border-amber-200 text-xs text-amber-800">
              <strong>Heads up:</strong> This will overwrite the current branding immediately. Pricing rules, dealer permissions, the enable toggle and webhook config are NOT touched.
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCopyModalOpen(false)}
                disabled={copyApplying}
                data-testid="copy-branding-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={applyCopyBranding}
                disabled={!copySelectedSourceId || copyApplying}
                data-testid="copy-branding-apply-btn"
              >
                {copyApplying ? "Copying…" : "Copy branding"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealerBrandingTab;
